import { useState } from "react";
import { searchTranscripts, resolveEntities, TranscriptSearchResult, ResolvedEntity } from "../api";

export default function SemanticSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TranscriptSearchResult[]>([]);
  const [entities, setEntities] = useState<ResolvedEntity[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setHasSearched(true);

    try {
      const [searchRes, entityRes] = await Promise.all([
        searchTranscripts(query).catch(() => ({ data: { results: [] as TranscriptSearchResult[] } })),
        resolveEntities(query).catch(() => ({ data: { entities: [] as ResolvedEntity[] } })),
      ]);
      setResults(searchRes.data.results);
      setEntities(entityRes.data.entities);
    } catch {
      setResults(DEMO_RESULTS);
      setEntities([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      <div style={{ padding: "20px 32px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Semantic Search
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(8,145,178,0.1)", color: "#0891b2" }}>
            + Entity Resolution
          </span>
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Search transcripts by meaning, not just keywords — with automatic entity linking
        </p>
      </div>

      <div style={{ padding: "16px 32px", flexShrink: 0 }}>
        {/* Search bar */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="e.g., 'What did the expert say about social isolation?' or 'Apple revenue forecast'"
            style={{
              flex: 1, padding: "10px 16px", borderRadius: 10,
              border: "1.5px solid rgba(0,0,0,0.08)", fontSize: 12,
              outline: "none", transition: "border 0.15s ease",
            }}
          />
          <button onClick={handleSearch} disabled={searching || !query.trim()} style={{
            padding: "10px 20px", borderRadius: 10, border: "none", cursor: "pointer",
            background: "#000", color: "#fff", fontSize: 12, fontWeight: 700,
            opacity: searching ? 0.5 : 1,
          }}>
            {searching ? "..." : "Search"}
          </button>
        </div>

        {/* Entity resolutions */}
        {entities.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            {entities.map((e, idx) => (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "4px 12px", borderRadius: 20,
                background: "rgba(8,145,178,0.06)", border: "1px solid rgba(8,145,178,0.15)",
              }}>
                <span style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", textDecoration: "line-through" }}>{e.mention}</span>
                <span style={{ fontSize: 10, color: "rgba(0,0,0,0.25)" }}>→</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#0891b2" }}>{e.canonical}</span>
                <span style={{ fontSize: 8, fontFamily: "'IBM Plex Mono', monospace", color: "rgba(0,0,0,0.25)" }}>
                  {(e.confidence * 100).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 32px 40px" }}>
        {!hasSearched ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.15 }}>~</div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.3)" }}>
              Search across all interview transcripts using natural language
            </div>
            <div style={{ fontSize: 10, color: "rgba(0,0,0,0.2)", marginTop: 6 }}>
              Uses TF-IDF cosine similarity — no vector database needed
            </div>
          </div>
        ) : results.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "rgba(0,0,0,0.3)", fontSize: 12 }}>
            No matching transcripts found — conduct more interviews to build the search index
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {results.map((r, idx) => (
              <div key={idx} style={{
                padding: "14px 18px", borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                borderLeft: `3px solid ${r.similarity > 0.5 ? "#10b981" : r.similarity > 0.3 ? "#f59e0b" : "rgba(0,0,0,0.15)"}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{r.expert || r.session_id}</span>
                  <div style={{
                    height: 6, flex: 1, maxWidth: 100, background: "rgba(0,0,0,0.04)", borderRadius: 3, overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", borderRadius: 3, background: r.similarity > 0.5 ? "#10b981" : r.similarity > 0.3 ? "#f59e0b" : "#ef4444",
                      width: `${r.similarity * 100}%`,
                    }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: r.similarity > 0.5 ? "#10b981" : "rgba(0,0,0,0.4)" }}>
                    {(r.similarity * 100).toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  fontSize: 11, lineHeight: 1.7, color: "rgba(0,0,0,0.5)",
                  padding: "8px 12px", borderRadius: 8, background: "rgba(0,0,0,0.02)",
                }}>
                  {r.preview}
                </div>
                {r.timestamp && (
                  <div style={{ fontSize: 9, color: "rgba(0,0,0,0.2)", marginTop: 6, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {r.session_id} | {r.timestamp}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DEMO_RESULTS: TranscriptSearchResult[] = [
  { session_id: "session_demo_1", expert: "Dr. Sarah Chen", similarity: 0.78, preview: "Social isolation in children is one of the strongest predictors I've seen in my practice. When a child withdraws from peer groups, especially after parental separation, we typically see a 60-70% correlation with negative adjustment outcomes...", timestamp: new Date().toISOString() },
  { session_id: "session_demo_2", expert: "Judge Michael Torres", similarity: 0.52, preview: "In my experience on the bench, the cases where children struggle most are high-conflict divorces. The parents' inability to co-parent effectively creates an environment of chronic stress that manifests in behavioral issues...", timestamp: new Date().toISOString() },
  { session_id: "session_demo_3", expert: "Prof. Linda Okafor", similarity: 0.34, preview: "The research literature strongly supports attachment theory as the primary framework. Secure attachment to at least one caregiver serves as a protective factor even in adverse circumstances...", timestamp: new Date().toISOString() },
];
